/**
 * Offline entry queue backed by IndexedDB.
 *
 * Problem: Endeavor workers log home visits from the road. Their phones often
 * sit on one bar of LTE, or no bars at all. If creating a trip requires a
 * live server round-trip, the trip silently fails and the worker moves on —
 * losing reimbursement dollars.
 *
 * Solution: when `POST /api/entries` fails with a network error, we stash the
 * payload in IndexedDB (survives page reloads, Safari backgrounding, phone
 * reboots) and render an optimistic "pending sync" row. On the next `online`
 * event (or app-mount), we replay the queue to the server in FIFO order.
 *
 * Limitations (v1 — intentional simplifications):
 *   - Only CREATE is queued. Editing or deleting a trip that was logged
 *     locally-only works (mutates the queued payload). Editing/deleting an
 *     already-synced trip while offline is not supported — the UI will
 *     surface an error and tell the worker to retry when online.
 *   - If a queued entry lands in a month that was approved before sync (rare
 *     but possible), the server returns 409 and we move that entry to a
 *     "failed" bucket, still visible in the UI so nothing is lost.
 *
 * This module has zero React/DOM imports so it can be unit-tested under
 * node's --experimental-vm-modules or jsdom if we add tests later.
 */

const DB_NAME = "endeavor-offline";
const DB_VERSION = 1;
const STORE_QUEUE = "entry_queue";
const STORE_FAILED = "entry_queue_failed";

// ---- Temp-ID helpers ---------------------------------------------------

const LOCAL_PREFIX = "local-";
export const isLocalId = (id) =>
  typeof id === "string" && id.startsWith(LOCAL_PREFIX);
const makeLocalId = () =>
  `${LOCAL_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ---- IDB plumbing ------------------------------------------------------

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      // SSR / tests / ancient browsers: no-op store.
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        db.createObjectStore(STORE_QUEUE, { keyPath: "localId" });
      }
      if (!db.objectStoreNames.contains(STORE_FAILED)) {
        db.createObjectStore(STORE_FAILED, { keyPath: "localId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function txStore(storeName, mode, fn) {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    let out;
    tx.oncomplete = () => resolve(out);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
    out = fn(store);
  });
}

function reqToPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---- Public API --------------------------------------------------------

/**
 * Queue a trip payload for later sync and return the optimistic entry shape
 * that the rest of the app can render immediately.
 */
export async function queueEntry(payload) {
  const localId = makeLocalId();
  const record = {
    localId,
    queuedAt: Date.now(),
    payload, // exact body we'd have POSTed
  };
  await txStore(STORE_QUEUE, "readwrite", (s) => s.put(record));
  return {
    id: localId,
    date: payload.date,
    origin: payload.origin,
    destination: payload.destination,
    miles: Number(payload.miles) || 0,
    parking: Number(payload.parking) || 0,
    tolls: Number(payload.tolls) || 0,
    purpose: payload.purpose,
    pending: true,
  };
}

/** Update a still-queued (local-only) entry in place. */
export async function updateQueuedEntry(localId, patch) {
  return txStore(STORE_QUEUE, "readwrite", async (s) => {
    const existing = await reqToPromise(s.get(localId));
    if (!existing) return;
    const merged = { ...existing, payload: { ...existing.payload, ...patch } };
    s.put(merged);
  });
}

/** Drop a still-queued (local-only) entry without ever sending it. */
export async function removeQueuedEntry(localId) {
  return txStore(STORE_QUEUE, "readwrite", (s) => s.delete(localId));
}

/** List every queued entry as an optimistic display row. */
export async function listQueuedEntries() {
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_QUEUE, "readonly");
    const store = tx.objectStore(STORE_QUEUE);
    const req = store.getAll();
    req.onsuccess = () =>
      resolve(
        (req.result || []).map((r) => ({
          id: r.localId,
          date: r.payload.date,
          origin: r.payload.origin,
          destination: r.payload.destination,
          miles: Number(r.payload.miles) || 0,
          parking: Number(r.payload.parking) || 0,
          tolls: Number(r.payload.tolls) || 0,
          purpose: r.payload.purpose,
          pending: true,
          queuedAt: r.queuedAt,
        }))
      );
    req.onerror = () => reject(req.error);
  });
}

/** List failed-sync entries (e.g. month-locked 409s). */
export async function listFailedEntries() {
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_FAILED, "readonly");
    const store = tx.objectStore(STORE_FAILED);
    const req = store.getAll();
    req.onsuccess = () =>
      resolve(
        (req.result || []).map((r) => ({
          id: r.localId,
          date: r.payload.date,
          origin: r.payload.origin,
          destination: r.payload.destination,
          miles: Number(r.payload.miles) || 0,
          parking: Number(r.payload.parking) || 0,
          tolls: Number(r.payload.tolls) || 0,
          purpose: r.payload.purpose,
          error: r.error,
          failed: true,
          queuedAt: r.queuedAt,
        }))
      );
    req.onerror = () => reject(req.error);
  });
}

export async function discardFailed(localId) {
  return txStore(STORE_FAILED, "readwrite", (s) => s.delete(localId));
}

/**
 * Drain the queue, POSTing every record to the server via the given sender.
 * Returns { flushed, failed }. Safe to call concurrently — a module-level
 * mutex prevents overlapping flushes from double-submitting.
 *
 * A network error on any record stops the flush early (the rest stays
 * queued, in order, for the next try). A server-side rejection (4xx/5xx
 * that's NOT a network fault) moves that single record to the failed store
 * and continues.
 */
let _flushInFlight = null;
export function flushQueue(sendFn) {
  if (_flushInFlight) return _flushInFlight;
  _flushInFlight = (async () => {
    let flushed = 0;
    let failed = 0;
    try {
      const db = await openDb();
      if (!db) return { flushed: 0, failed: 0 };
      const queued = await listQueuedEntries();
      // FIFO — oldest first.
      queued.sort((a, b) => (a.queuedAt || 0) - (b.queuedAt || 0));
      for (const entry of queued) {
        const record = await txStore(STORE_QUEUE, "readonly", (s) =>
          reqToPromise(s.get(entry.id))
        );
        if (!record) continue;
        try {
          await sendFn(record.payload);
          await txStore(STORE_QUEUE, "readwrite", (s) => s.delete(entry.id));
          flushed++;
        } catch (e) {
          if (isNetworkError(e)) {
            // Keep it queued; we'll retry when the connection is back.
            break;
          }
          // Server rejected it — bank the record in the failed store so the
          // user can see it and decide.
          await txStore(STORE_FAILED, "readwrite", (s) =>
            s.put({ ...record, error: e.message || String(e) })
          );
          await txStore(STORE_QUEUE, "readwrite", (s) => s.delete(entry.id));
          failed++;
        }
      }
    } finally {
      _flushInFlight = null;
    }
    return { flushed, failed };
  })();
  return _flushInFlight;
}

/** How many trips are still awaiting sync. */
export async function pendingCount() {
  const db = await openDb();
  if (!db) return 0;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_QUEUE, "readonly");
    const req = tx.objectStore(STORE_QUEUE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function isNetworkError(err) {
  // Our fetch wrapper throws `TypeError` with "Failed to fetch" on offline
  // and our own Error with a numeric .status on server faults.
  if (!err) return false;
  if (typeof err.status === "number") return false;
  if (err.name === "TypeError") return true;
  const msg = String(err.message || "").toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed")
  );
}
